import { useEffect, useState } from 'react';
import { Page, Table, PageTitleWithButton, Pagination } from '../../../../components';
import { useSnackbar } from 'notistack';
import { Button as SMButton, Theme, Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  deleteStandardFeature,
  getStandardFeatureDetail,
  getStandardFeaturesList
} from '../../../../fetch';
import { IStandardFeatureDetail, IStandardFeatureList } from '../../../../models';
import { StandardFeatureFilters } from './standard-feature-filters';
import { StandardFeatureModal } from './standard-feature-modal';
import { useQuery } from 'react-query';

export const StandardFeatures = () => {
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();
  const [standardFeatures, setStandardFeatures] = useState<IStandardFeatureList[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingStandardFeatures, setLoadingStandardFeatures] = useState<boolean>(true);
  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    name?: string;
    optionSet?: string;
    phase?: string;
  }>({});
  const [sortDirection, setSortDirection] = useState<{
    optionSet?: 'Asc' | 'Desc';
    name?: 'Asc' | 'Desc';
    phase?: 'Asc' | 'Desc';
    alwaysDisplayInOptionsReport?: 'Asc' | 'Desc';
  }>({
    optionSet: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('optionSet');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStandardFeatureId, setSelectedStandardFeatureId] = useState<string>('');

  const fetchStandardFeatures = async () => {
    try {
      setLoadingStandardFeatures(true);
      const res = await getStandardFeaturesList({
        page: page + 1,
        perPage: perPage,
        name: nameSearch,
        optionSetId: selectedOptionId ? Number(selectedOptionId) : null,
        phaseId: selectedPhaseId ? Number(selectedPhaseId) : null,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      setRecordCount(res.totalRecordCount);
      setStandardFeatures(res.records);
    } catch (error) {
      enqueueSnackbar(
        error?.Detail ?? 'Error loading Standard Features, please refresh and try again.',
        {
          variant: 'error'
        }
      );
    } finally {
      setLoadingStandardFeatures(false);
    }
  };

  const {
    isLoading: isLoadingStandardFeature,
    data: standardFeatureDetail,
    refetch: refetchStandardFeature
  } = useQuery<IStandardFeatureDetail, Error>(
    ['standard-feature-detail', selectedStandardFeatureId],
    () => getStandardFeatureDetail(selectedStandardFeatureId),
    {
      enabled: selectedStandardFeatureId !== null && selectedStandardFeatureId !== 'Add'
    }
  );

  useEffect(() => {
    fetchStandardFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStandardFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection, filters]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedStandardFeatureId('');
    refetchStandardFeature();
  };

  const handleEditOption = option => {
    setSelectedStandardFeatureId(option);
    setIsModalOpen(true);
  };

  const handleDeleteStandardFeature = async standardFeatureId => {
    await deleteStandardFeature(standardFeatureId);
    await fetchStandardFeatures();
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = [
    {
      Header: 'Option Set',
      accessor: 'optionSetName',
      isServerSorted: selectedSort === 'optionSet',
      isServerSortedDesc: sortDirection.optionSet === 'Desc',
      handleClickColumn: () => handleClickColumn('optionSet')
    },
    {
      Header: 'Name',
      accessor: 'name',
      isServerSorted: selectedSort === 'name',
      isServerSortedDesc: sortDirection.name === 'Desc',
      handleClickColumn: () => handleClickColumn('name')
    },
    {
      Header: 'Phase',
      accessor: 'phaseName',
      isServerSorted: selectedSort === 'phase',
      isServerSortedDesc: sortDirection.phase === 'Desc',
      handleClickColumn: () => handleClickColumn('phase')
    },
    {
      Header: 'Always Display in Options Report',
      accessor: 'alwaysDisplayInOptionsReport',
      isServerSorted: selectedSort === 'alwaysDisplayInOptionsReport',
      isServerSortedDesc: sortDirection.alwaysDisplayInOptionsReport === 'Desc',
      handleClickColumn: () => handleClickColumn('alwaysDisplayInOptionsReport'),
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IStandardFeatureList } };
      }) => {
        return <>{original.alwaysDisplayInOptionsReport === true ? 'Yes' : 'No'}</>;
      }
    },
    {
      Header: 'Upgrade Selections',
      accessor: 'upgradeSelections',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }) => {
        return (
          <div
            style={{
              maxWidth: '500px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={
              original.upgradeOptions && original.upgradeOptions.length > 0
                ? original.upgradeOptions.map(upgrade => upgrade.optionName).join(', ')
                : ''
            }
          >
            {original.upgradeOptions && original.upgradeOptions.length > 0
              ? original.upgradeOptions.map(upgrade => upgrade.optionName).join(', ')
              : ''}
          </div>
        );
      }
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IStandardFeatureList } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <SMButton
              id="edit-framer"
              type="button"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditOption(original.standardFeatureId)}
            >
              Edit
            </SMButton>
            <SMButton
              id="delete-framer"
              type="button"
              className={classes.deleteButton}
              startIcon={<Delete />}
              onClick={async () => {
                const result = window.confirm(
                  'Are you sure you want to delete this Standard Feature type?'
                );
                if (result) {
                  try {
                    await handleDeleteStandardFeature(original.standardFeatureId);
                    enqueueSnackbar('Standard Fetaure Type Deleted!', {
                      variant: 'success'
                    });
                    fetchStandardFeatures();
                  } catch (error) {
                    console.log(error);
                    enqueueSnackbar(
                      error?.Detail ?? 'Error deleting Standard Feature type, please try again',
                      {
                        variant: 'error'
                      }
                    );
                  }
                }
              }}
            >
              Delete
            </SMButton>
          </div>
        );
      }
    }
  ];

  return (
    <Page title="Standard Features">
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Standard Features'
        }}
        button={
          <Box display="flex">
            <Button
              id="add-option"
              startIcon={<Add />}
              color="primary"
              variant="contained"
              className={classes.addStandardFeatureButton}
              onClick={() => {
                setSelectedStandardFeatureId('add');
                setIsModalOpen(true);
              }}
            >
              Add Standard Feature
            </Button>
            {/* </Link> */}
          </Box>
        }
      />
      <StandardFeatureFilters
        isLoading={isLoadingStandardFeatures}
        handleNameSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            ...filters,
            name: nameSearch
          });

          if (clearSearch) {
            setNameSearch('');
          }
        }}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              name: '',
              optionSet: '',
              phase: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters
            });
          }
        }}
        setPage={setPage}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        searchName={nameSearch}
        setSearchName={setNameSearch}
        selectedPhaseId={selectedPhaseId}
        setSelectedPhaseId={setSelectedPhaseId}
        selectedOptionId={selectedOptionId}
        setSelectedOptionId={setSelectedOptionId}
      />
      <Table
        columns={columns}
        data={standardFeatures}
        isLoading={isLoadingStandardFeatures}
        hidePagination
        mobileProps={{
          handleEdit: (option: any) => handleEditOption(standardFeatures)
        }}
      />
      {!isLoadingStandardFeatures && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <StandardFeatureModal
        open={isModalOpen}
        standardFeatureDetail={standardFeatureDetail}
        standardFeatureId={selectedStandardFeatureId}
        refetchstandardFeatureDetail={refetchStandardFeature}
        refetchstandardFeatures={fetchStandardFeatures}
        modalIsLoading={isLoadingStandardFeature}
        onClose={handleModalClose}
      />
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addStandardFeatureButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
